import axios from 'axios';
import auth from './auth';

const API_ENDPOINT = process.env.VUE_APP_API_ENDPOINT;

// Axiosインスタンスの作成
const apiClient = axios.create({
  baseURL: API_ENDPOINT,
  headers: {
    'Content-Type': 'application/json',
  },
});

// リクエストインターセプター（認証トークンを自動的に付与）
apiClient.interceptors.request.use(
  async (config) => {
    try {
      const token = await auth.getIdToken();
      config.headers.Authorization = token;
    } catch (error) {
      console.warn('Failed to get auth token:', error);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// レスポンスインターセプター
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response && error.response.status === 401) {
      // 認証エラーの場合、ログイン画面にリダイレクト
      auth.signOut();
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default {
  // Fields API
  async getFields() {
    const response = await apiClient.get('/fields');
    return response.data;
  },

  async getFieldById(id) {
    const response = await apiClient.get(`/fields/${id}`);
    return response.data;
  },

  async createField(field) {
    const response = await apiClient.post('/fields', field);
    return response.data;
  },

  async updateField(id, field) {
    const response = await apiClient.put(`/fields/${id}`, field);
    return response.data;
  },

  async deleteField(id) {
    const response = await apiClient.delete(`/fields/${id}`);
    return response.data;
  },

  // Events API
  async getEvents(fieldId) {
    const response = await apiClient.get('/events', {
      params: { fieldId },
    });
    return response.data;
  },

  async createEvent(event) {
    const response = await apiClient.post('/events', event);
    return response.data;
  },

  async updateEvent(fieldId, eventId, event) {
    const response = await apiClient.put(`/events/${fieldId}/${eventId}`, event);
    return response.data;
  },

  async deleteEvent(fieldId, eventId) {
    const response = await apiClient.delete(`/events/${fieldId}/${eventId}`);
    return response.data;
  },

  // Costs API
  async getCosts() {
    const response = await apiClient.get('/costs');
    return response.data;
  },

  // AI Analysis API
  async startAnalysis(fieldId, dateFrom, dateTo) {
    const response = await apiClient.post(`/fields/${fieldId}/analyze`, {
      fieldId,
      dateFrom,
      dateTo
    });
    return response.data; // { analysisId, status: 'processing', message: '...' }
  },

  async getAnalysisResult(analysisId) {
    const response = await apiClient.get(`/analysis/${analysisId}`);
    return response.data; // { analysisId, status, result, errorMessage, ... }
  },

  // ポーリングで分析結果を取得（フロントエンドで使いやすいラッパー）
  async analyzeFieldWithPolling(fieldId, dateFrom, dateTo, onProgress) {
    // 分析開始
    const startResponse = await this.startAnalysis(fieldId, dateFrom, dateTo);
    const { analysisId } = startResponse;

    if (onProgress) onProgress({ status: 'processing', message: '分析を開始しました...' });

    // ポーリング（最大10分、5秒間隔）
    const maxAttempts = 120; // 10分 = 120 * 5秒
    const pollInterval = 5000; // 5秒

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      await new Promise(resolve => setTimeout(resolve, pollInterval));

      const result = await this.getAnalysisResult(analysisId);

      if (result.status === 'completed') {
        if (onProgress) onProgress({ status: 'completed', message: '分析が完了しました' });
        return {
          analysis: result.result,
          usage: result.usage
        };
      } else if (result.status === 'error') {
        throw new Error(result.errorMessage || '分析中にエラーが発生しました');
      }

      // まだprocessing中
      if (onProgress) {
        onProgress({ 
          status: 'processing', 
          message: `分析中... (${Math.floor((attempt + 1) * pollInterval / 1000)}秒経過)` 
        });
      }
    }

    throw new Error('分析がタイムアウトしました。時間をおいて再度お試しください。');
  },
};
